<template>
  <div v-for="group in filterGroups" v-show="group.items.length > 0" :key="group.name" class="mt-8">
    <h3 class="mb-5 font-medium">{{ $t(`pages.profile-shop.filters.${group.name}.title`) }}</h3>

    <template v-if="group.type === 'CHECKBOXES'">
      <div class="filter-container">
        <TransitionGroup name="filter-list" tag="div" class="space-y-3">
          <div v-for="item in group.items" :key="item.value">
            <IAICheckbox
              :id="item.value"
              type="primary"
              :model-value="includesFilter(group.name, item.value)"
              @update:modelValue="toggleFilter(group.name, item.value, ToggleFilterSource.FilterPanel)"
            >
              {{ item.label }}
            </IAICheckbox>
          </div>
        </TransitionGroup>

        <template v-if="group.restItems?.length">
          <Transition name="expand">
            <div v-if="isShowMoreOpen[group.name]" class="mt-3">
              <TransitionGroup name="filter-list" tag="div" class="space-y-3">
                <div v-for="item in group.restItems" :key="item.value">
                  <IAICheckbox
                    :id="item.value"
                    type="primary"
                    :model-value="includesFilter(group.name, item.value)"
                    @update:modelValue="toggleFilter(group.name, item.value, ToggleFilterSource.FilterPanel)"
                  >
                    {{ item.label }}
                  </IAICheckbox>
                </div>
              </TransitionGroup>
            </div>
          </Transition>

          <IAIButton
            class="mt-4"
            :label="
              isShowMoreOpen[group.name]
                ? $t('pages.profile-shop.filters.show-less')
                : $t('pages.profile-shop.filters.show-all')
            "
            variant="link"
            size="small"
            @click="isShowMoreOpen[group.name] = !isShowMoreOpen[group.name]"
          />
        </template>
      </div>
    </template>

    <template v-else-if="group.type === 'SLIDER'">
      <IAISlider
        class="mb-16"
        :model-value="group.items"
        :min="group.min"
        :max="group.max"
        :current-tooltip="group.currentTooltip"
        @update:modelValue="Array.isArray($event) && updatePriceRange($event)"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { IAIButton, IAICheckbox, IAISlider } from "@/core-ui-vue";
import { priceDisplay, priceAsNumber } from "@/features/pricing/components/IAIPrice/IAIPrice.vue";
import { FilterKey, useProfileShopFilters } from "../../composables/useProfileShopFilters";
import { computed, reactive } from "vue";
import { FilterOption } from "@/features/profile-shop/services/api";
import { ToggleFilterSource } from "@/features/profile-shop/composables/useProfileShopFilters";
import { getUnicodeBCP47 } from "@/locales/shared";
import { groupFilterItems, sortFilterItems } from "@/features/profile-shop/utils";

interface CheckboxFilterGroup {
  name: "type" | "style" | "format" | "genres";
  type: "CHECKBOXES";
  items: FilterOption[];
  restItems?: FilterOption[];
}

interface SliderFilterGroup {
  name: "price";
  type: "SLIDER";
  min: number;
  max: number;
  items: number[];
  currentTooltip: string[];
}

type FilterGroup = CheckboxFilterGroup | SliderFilterGroup;

const { filtersList, activeFilters, toggleFilter, updatePriceRange, includesFilter } = useProfileShopFilters();
const lang = getUnicodeBCP47();

const isShowMoreOpen = reactive<Partial<Record<FilterKey, boolean>>>({
  style: false,
});

const filterGroups = computed(() => {
  const groups: FilterGroup[] = [
    {
      name: "type",
      type: "CHECKBOXES",
      items: sortFilterItems(filtersList.type, "type", includesFilter),
    },
    {
      name: "style",
      type: "CHECKBOXES",
      ...groupFilterItems(filtersList.style, "style", includesFilter),
    },
    {
      name: "format",
      type: "CHECKBOXES",
      items: sortFilterItems(filtersList.format, "format", includesFilter),
    },
    {
      name: "price",
      type: "SLIDER",
      min: priceAsNumber(filtersList.price.min),
      max: priceAsNumber(filtersList.price.max),
      items: [priceAsNumber(activeFilters.value.price.min), priceAsNumber(activeFilters.value.price.max)],
      currentTooltip: [
        priceDisplay(activeFilters.value.price.min, lang, false),
        priceDisplay(activeFilters.value.price.max, lang, false),
      ],
    },
    {
      name: "genres",
      type: "CHECKBOXES",
      items: sortFilterItems(filtersList.genres, "genres", includesFilter),
    },
  ];
  return groups;
});
</script>

<style scoped lang="scss">
.filter-container {
  position: relative;
}

/* List transitions */
.filter-list-move {
  transition: transform 0.5s ease;
}

.filter-list-enter-active {
  transition: all 0.4s ease-out;
}

.filter-list-leave-active {
  transition: all 0.4s ease-in;
  position: absolute;
  width: 100%;
}

.filter-list-enter-from,
.filter-list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Expand/collapse transition for "show more" section */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-out;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}
</style>
